import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";

function ProjectWizard() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [step, setStep] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) navigate("/login");
  }, [navigate]);

  const [formData, setFormData] = useState({
    client: {
      client_name: "",
      contact_person: "",
      email: "",
      phone: "",
      address: "",
      industry_type: "",
    },
    project: {
      project_name: "",
      Address: "",
      start_date: "",
      end_date: "",
      status: "Planned",
      project_type: "Residential",
      description: "",
      Concerned_person: "",
      Bank_Guarantee_amount: "",
      Bank_Guarantee_date: "",
      EMD_amount: "",
      EMD_date: "",
    },
    consultant: {
      name: "",
      specialty: "Structural",
      hourly_rate: "",
      contact_email: "",
      phone: "",
    },
    task: {
      task_name: "",
      phase: "Planning",
      start_date: "",
      end_date: "",
      status: "Not Started",
    },
    financial: {
      budget_allocated: "",
      actual_cost: "",
      consultant_fees: "",
      revenue_from_client: "",
      material_cost: "",
      labor_cost: "",
      travel_cost: "",
    },
    feedback: {
      rating: "",
      comments: "",
    },
  });

  const handleChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    try {
      const res = await fetch(`${baseUrl}/submit_project`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          role: user?.role || "Viewer",
        }),
      });

      const text = await res.text();
      const data = JSON.parse(text);

      if (data.success) {
        alert("Project Submitted Successfully!");
        navigate("/dashboard");
      } else {
        alert("Error submitting project");
      }
    } catch (err) {
      console.error("Submit Error:", err);
      alert("Server error while submitting project.");
    }
  };

  const next = () => setStep((prev) => Math.min(prev + 1, 3));
  const back = () => setStep((prev) => Math.max(prev - 1, 1));

  return (
    <div className="min-h-screen bg-gray-100">
      <Header toggleSidebar={toggleSidebar} />
      <Sidebar open={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="max-w-4xl mx-auto mt-8 bg-white p-6 rounded shadow relative">
        {/* Title and Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-xl font-bold text-center sm:text-left">
            Step {step} of 3 – Project Setup
          </h2>
          <button
            className="mt-2 sm:mt-0 text-sm text-blue-600 hover:underline"
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </button>
        </div>

        {/* Step 1: Client + Project */}
        {step === 1 && (
          <>
            <h3 className="font-semibold text-lg mb-2">Client Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(formData.client).map(([key, value]) => (
                <input
                  key={key}
                  className="p-2 border border-gray-300 rounded"
                  placeholder={key.replace(/_/g, " ")}
                  value={value}
                  onChange={(e) => handleChange("client", key, e.target.value)}
                />
              ))}
            </div>

            <h3 className="font-semibold text-lg mb-2">Project Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.project).map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  {key.includes("date") && (
                    <label className="text-sm text-gray-400 mb-1 capitalize">
                      {key.replace(/_/g, " ")}
                    </label>
                  )}
                  <input
                    className="p-2 border border-gray-300 rounded"
                    value={value}
                    placeholder={!key.includes("date") ? key.replace(/_/g, " ") : undefined}
                    type={key.includes("date") ? "date" : "text"}
                    onChange={(e) => handleChange("project", key, e.target.value)}
                  />
                </div>
              ))}

            </div>
          </>
        )}

        {/* Step 2: Consultant + Task */}
        {step === 2 && (
          <>
            <h3 className="font-semibold text-lg mb-2">Consultant Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(formData.consultant).map(([key, value]) => (
                <input
                  key={key}
                  className="p-2 border border-gray-300 rounded"
                  placeholder={key.replace(/_/g, " ")}
                  value={value}
                  onChange={(e) =>
                    handleChange("consultant", key, e.target.value)
                  }
                />
              ))}
            </div>

            <h3 className="font-semibold text-lg mb-2">Task Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(formData.task).map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  {key.includes("date") && (
                    <label className="text-gray-700 text-sm mb-1 capitalize">
                      {key.replace(/_/g, " ")}
                    </label>
                  )}
                  <input
                    className="p-2 border border-gray-300 rounded"
                    value={value}
                    type={key.includes("date") ? "date" : "text"}
                    placeholder={!key.includes("date") ? key.replace(/_/g, " ") : undefined}
                    onChange={(e) => handleChange("task", key, e.target.value)}
                  />
                </div>
              ))}

            </div>
          </>
        )}

        {/* Step 3: Financial + Feedback */}
        {step === 3 && (
          <>
            <h3 className="font-semibold text-lg mb-2">Financial Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(formData.financial).map(([key, value]) => (
                <input
                  key={key}
                  className="p-2 border border-gray-300 rounded"
                  placeholder={key.replace(/_/g, " ")}
                  value={value}
                  onChange={(e) =>
                    handleChange("financial", key, e.target.value)
                  }
                />
              ))}
            </div>

            <h3 className="font-semibold text-lg mb-2">Feedback (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="p-2 border border-gray-300 rounded"
                placeholder="Rating (1–5)"
                value={formData.feedback.rating}
                onChange={(e) =>
                  handleChange("feedback", "rating", e.target.value)
                }
              />
              <input
                className="p-2 border border-gray-300 rounded"
                placeholder="Comments"
                value={formData.feedback.comments}
                onChange={(e) =>
                  handleChange("feedback", "comments", e.target.value)
                }
              />
            </div>
          </>
        )}

        <div className="flex justify-between mt-6">
          {step > 1 && (
            <button
              className="bg-gray-400 text-white px-4 py-2 rounded"
              onClick={back}
            >
              Back
            </button>
          )}
          {step < 3 ? (
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={next}
            >
              Next
            </button>
          ) : (
            <button
              className="bg-green-600 text-white px-4 py-2 rounded"
              onClick={handleSubmit}
            >
              Submit Project
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectWizard;
